import {
  TEMPLATE_CATEGORY_FIELD_NAMES,
  TEMPLATE_FIELD_NAMES,
  TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES
} from '../constants/issue-templates';
import has from 'lodash/has';
import lodashGet from 'lodash/get';
import lodashOmitBy from 'lodash/omitBy';
import isNil from 'lodash/isNil';
import isNumber from 'lodash/isNumber';
import {STATUS_OPEN} from '../constants/status-types';
import {getLocalStorageByKey} from './localStorage-helpers';
import i18n from './i18n';
import {maxLengthValidator, TEMPLATE_CATEGORY_TITLE_MAX_LENGTH, uniqueValueValidator} from './validation-helpers';
import {
  getTemplateCategoriesInDeleteProgressMap,
  getTemplateCategoriesInFetchTemplatesProgress,
  getTemplateCategoriesInUpdateProgressMap
} from '../app/templates/template-selectors';

/**
 * This method parses pending template into BE structure.
 * @param formValues
 * @param fieldIssueTypeEntitiesMap
 * @param issueCustomAttributesMappings
 * @returns {{}}
 */
export const parsePendingTemplateToBeStructure = (
  formValues,
  fieldIssueTypeEntitiesMap,
  issueCustomAttributesMappings
) => {
  const customAttributeValues = [];
  const subType = fieldIssueTypeEntitiesMap[formValues[TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.SUBTYPE_ID]];
  if (subType) {
    const customAttributeIds = subType.mappingIds.map(
      mappingId => issueCustomAttributesMappings[mappingId].attributeDefinitionId
    );
    customAttributeIds.forEach(attributeDefinitionId => {
      if (has(formValues, [TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.CUSTOM_ATTRIBUTES, attributeDefinitionId])) {
        customAttributeValues.push({
          attributeDefinitionId,
          value: formValues[TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.CUSTOM_ATTRIBUTES][attributeDefinitionId]
        });
      }
    });
  }

  const templateIssueAttributes = {
    [TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.TITLE]: formValues[TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.TITLE],
    [TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.SUBTYPE_ID]: formValues[TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.SUBTYPE_ID],
    [TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.STATUS]: formValues[TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.STATUS],
    [TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.ASSIGNED_TO]: formValues[TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.ASSIGNED_TO],
    [TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.ASSIGNED_TO_TYPE]:
      formValues[TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.ASSIGNED_TO_TYPE],
    [TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.DUE_DATE]: isNumber(
      formValues[TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.DUE_DATE]
    )
      ? formValues[TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.DUE_DATE]
      : null,
    [TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.LOCATION_ID]: formValues[TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.LOCATION_ID],
    [TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.LOCATION_DETAILS]:
      formValues[TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.LOCATION_DETAILS],
    [TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.OWNER_ID]: formValues[TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.OWNER_ID],
    [TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.ROOT_CAUSE_ID]:
      formValues[TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.ROOT_CAUSE_ID],
    [TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.DESCRIPTION]: formValues[TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.DESCRIPTION],
    [TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.CUSTOM_ATTRIBUTES]: customAttributeValues
  };

  return {
    [TEMPLATE_FIELD_NAMES.CATEGORY_ID]: formValues[TEMPLATE_FIELD_NAMES.CATEGORY_ID],
    [TEMPLATE_FIELD_NAMES.IS_ACTIVE]: formValues[TEMPLATE_FIELD_NAMES.IS_ACTIVE],
    [TEMPLATE_FIELD_NAMES.ISSUE_ATTRIBUTES]: lodashOmitBy(templateIssueAttributes, isNil)
  };
};

export const parseTemplateToPendingTemplateForm = template => {
  if (!template) {
    return {};
  }
  const customAttributes = {};
  const templateIssueAttributes = template[TEMPLATE_FIELD_NAMES.ISSUE_ATTRIBUTES];
  if (templateIssueAttributes[TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.CUSTOM_ATTRIBUTES]) {
    templateIssueAttributes[TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.CUSTOM_ATTRIBUTES].forEach(attr => {
      customAttributes[attr.attributeDefinitionId] = attr.value;
    });
  }

  return {
    [TEMPLATE_FIELD_NAMES.CATEGORY_ID]: template[TEMPLATE_FIELD_NAMES.CATEGORY_ID],
    [TEMPLATE_FIELD_NAMES.IS_ACTIVE]: template[TEMPLATE_FIELD_NAMES.IS_ACTIVE],
    [TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.TITLE]: templateIssueAttributes[TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.TITLE],
    [TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.SUBTYPE_ID]:
      templateIssueAttributes[TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.SUBTYPE_ID],
    [TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.STATUS]:
      templateIssueAttributes[TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.STATUS],
    [TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.ASSIGNED_TO]:
      templateIssueAttributes[TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.ASSIGNED_TO],
    [TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.ASSIGNED_TO_TYPE]:
      templateIssueAttributes[TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.ASSIGNED_TO_TYPE],
    [TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.DUE_DATE]:
      templateIssueAttributes[TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.DUE_DATE],
    [TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.LOCATION_ID]:
      templateIssueAttributes[TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.LOCATION_ID],
    [TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.LOCATION_DETAILS]:
      templateIssueAttributes[TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.LOCATION_DETAILS],
    [TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.OWNER_ID]:
      templateIssueAttributes[TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.OWNER_ID],
    [TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.ROOT_CAUSE_ID]:
      templateIssueAttributes[TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.ROOT_CAUSE_ID],
    [TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.DESCRIPTION]:
      templateIssueAttributes[TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.DESCRIPTION],
    [TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.CUSTOM_ATTRIBUTES]: customAttributes
  };
};

export const doesTemplateCategoryAllowTemplateToToggleIsActive = (template, templateCategoriesEntitiesMap) => {
  const categoryId = template[TEMPLATE_FIELD_NAMES.CATEGORY_ID];
  if (!categoryId) {
    return true;
  }
  return lodashGet(templateCategoriesEntitiesMap, [categoryId, TEMPLATE_CATEGORY_FIELD_NAMES.IS_ACTIVE], true);
};

const handleInactiveTemplateInitialFormValue = (initialFormValues, attribute, entitiesMap) => {
  if (initialFormValues[attribute]) {
    const entity = entitiesMap[initialFormValues[attribute]];
    if (entity && !entity.isActive) {
      delete initialFormValues[attribute];
    }
  }
};

export const getTemplateValidInitialFieldValues = (
  initialValues,
  pendingTemplate,
  localStorageKey,
  fieldIssueTypeEntitiesMap,
  defaultIssueSubtypeId,
  templateCategoriesEntitiesMap,
  rootCauseEntitiesMap
) => {
  let localStorageData;
  const defaultValues = {
    [TEMPLATE_FIELD_NAMES.IS_ACTIVE]: true,
    [TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.STATUS]: STATUS_OPEN,
    [TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.SUBTYPE_ID]: defaultIssueSubtypeId
  };
  if (localStorageKey) {
    localStorageData = getLocalStorageByKey(localStorageKey);
  }
  const initialFormValues = {...defaultValues, ...localStorageData, ...pendingTemplate, ...initialValues};

  handleInactiveTemplateInitialFormValue(
    initialFormValues,
    TEMPLATE_FIELD_NAMES.CATEGORY_ID,
    templateCategoriesEntitiesMap
  );
  handleInactiveTemplateInitialFormValue(
    initialFormValues,
    TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.SUBTYPE_ID,
    fieldIssueTypeEntitiesMap
  );
  handleInactiveTemplateInitialFormValue(
    initialFormValues,
    TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.ROOT_CAUSE_ID,
    rootCauseEntitiesMap
  );
  return initialFormValues;
};

const getIsTemplateCategoryInUpdateProgress = (state, id) => {
  const map = getTemplateCategoriesInUpdateProgressMap(state);
  return !!map[id];
};

const getIsTemplateCategoryInDeleteProgress = (state, id) => {
  const map = getTemplateCategoriesInDeleteProgressMap(state);
  return !!map[id];
};

const getIsTemplateCategoryFetchingTemplatesForDelete = (state, id) => {
  const map = getTemplateCategoriesInFetchTemplatesProgress(state);
  return !!map[id];
};

export const getIsTemplateCategoryLoading = (state, id) =>
  getIsTemplateCategoryInUpdateProgress(state, id) ||
  getIsTemplateCategoryFetchingTemplatesForDelete(state, id) ||
  getIsTemplateCategoryInDeleteProgress(state, id);

export const getIssueTemplateCategoryValidators = templateCategoryTitles => [
  {
    message: i18n.t('new.maxCharactersValidationMessage', {limit: TEMPLATE_CATEGORY_TITLE_MAX_LENGTH}),
    validate: maxLengthValidator(TEMPLATE_CATEGORY_TITLE_MAX_LENGTH)
  },
  {
    message: i18n.t('createCategory.title.categoryExists'),
    validate: uniqueValueValidator(templateCategoryTitles)
  }
];
